import {
  faArrowRight,
  faCheck,
  faDownload,
  faFileArchive,
  faFilePdf,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOutsideClick } from "../../util/handlers";
import { useWorkspaces } from "../state";
import { Cube } from "../state/types";
import { isExploration } from "../workspaces/kind";
import { parcelPayload } from "../workspaces/parcelPayload";
import { TagButton } from "../workspaces/TagButton";
import { fCoords } from "./sections/util";

const btnBase = "inline-flex items-center gap-1.5 rounded-md text-sm font-bold px-4 py-2";

const AddToActiveWorkspace = ({ field }: { field: Cube }) => {
  const { workspaces, selectedWorkspaceId, addParcelToWorkspace } = useWorkspaces();
  const [added, setAdded] = useState(false);

  const workspace = workspaces?.find((p) => p.id === selectedWorkspaceId);
  if (!workspace) return null;

  const existingParcel = workspace.parcels?.find((p) => p.name === field.name);
  const alreadyInWorkspace = !!existingParcel;

  const handleAdd = async () => {
    const success = await addParcelToWorkspace(workspace.id, parcelPayload(field));
    if (success) {
      setAdded(true);
      setTimeout(() => setAdded(false), 2000);
    }
  };

  if (alreadyInWorkspace || added) {
    return (
      <div className="flex items-center gap-2">
        <span className={`${btnBase} text-green`}>
          <FontAwesomeIcon icon={faCheck} />
          <span className="truncate max-w-40">In {workspace.name}</span>
        </span>
        {existingParcel && (
          <TagButton parcel={existingParcel} workspaceId={workspace.id} variant="full" />
        )}
      </div>
    );
  }

  return (
    <button onClick={handleAdd} className={`${btnBase} bg-bright-yellow hover:bg-yellow`}>
      <FontAwesomeIcon icon={faPlus} className="shrink-0" />
      <span className="truncate max-w-40">{workspace.name}</span>
    </button>
  );
};

const AddToWorkspace = ({ field }: { field: Cube }) => {
  const {
    workspaces,
    selectedWorkspaceId,
    addParcelToWorkspace,
    removeParcelFromWorkspace,
    createWorkspace,
    selectWorkspace,
  } = useWorkspaces();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [added, setAdded] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (selectedWorkspaceId) return <AddToActiveWorkspace field={field} />;

  const closePopup = () => {
    setOpen(false);
    setCreating(false);
    setNewName("");
    setError(null);
  };

  const handleAdd = async (workspaceId: number) => {
    const success = await addParcelToWorkspace(workspaceId, parcelPayload(field));
    closePopup();
    if (success) {
      setAdded(true);
      setTimeout(() => setAdded(false), 2000);
    }
  };

  const handleRemove = async (workspaceId: number, parcelId: number) => {
    await removeParcelFromWorkspace(workspaceId, parcelId);
    closePopup();
  };

  const handleOpenWorkspace = (workspaceId: number) => {
    selectWorkspace(workspaceId);
    navigate(`/workspaces/${workspaceId}`);
    closePopup();
  };

  const handleCreateAndAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    setError(null);
    const workspace = await createWorkspace(name);
    if (!workspace?.id) {
      setError("Kon project niet aanmaken");
      setBusy(false);
      return;
    }
    const success = await addParcelToWorkspace(workspace.id, parcelPayload(field));
    setBusy(false);
    closePopup();
    if (success) {
      setAdded(true);
      setTimeout(() => setAdded(false), 2000);
    }
  };

  const containingWorkspaces =
    workspaces?.filter((p) => p.parcels?.some((pc) => pc.name === field.name)) ?? [];
  const inWorkspacesLabel =
    containingWorkspaces.length === 1
      ? `In ${containingWorkspaces[0].name}`
      : containingWorkspaces.length > 1
        ? `In ${containingWorkspaces.length} projecten`
        : null;

  return (
    <div className="relative">
      <button
        onClick={() => (open ? closePopup() : setOpen(true))}
        className={`${btnBase} bg-bright-yellow hover:bg-yellow`}
      >
        <FontAwesomeIcon
          icon={added || inWorkspacesLabel ? faCheck : faPlus}
          className="shrink-0"
        />
        <span className="truncate max-w-48">
          {added ? "Toegevoegd" : (inWorkspacesLabel ?? "Toevoegen aan project")}
        </span>
      </button>
      {open && workspaces && (
        <div className="absolute bottom-11 left-0 bg-white rounded-md shadow-lg border z-50 min-w-[220px]">
          {workspaces.length === 0 && !creating ? (
            <div className="p-3 text-sm text-gray-500">Nog geen projecten</div>
          ) : (
            workspaces.map((workspace) => {
              const existing = workspace.parcels?.find((pc) => pc.name === field.name);
              if (existing) {
                return (
                  <div
                    key={workspace.id}
                    className="flex items-stretch border-b border-gray-100 last:border-b-0 first:rounded-t-md bg-green/10"
                  >
                    <button
                      onClick={() => handleRemove(workspace.id, existing.id)}
                      title={`Verwijder uit ${workspace.name}`}
                      className="group flex items-center gap-2 flex-1 min-w-0 text-left p-2 pr-4 text-sm text-green hover:bg-red-50 hover:text-red-600"
                    >
                      <FontAwesomeIcon icon={faCheck} className="group-hover:hidden" />
                      <FontAwesomeIcon icon={faTrash} className="hidden group-hover:inline" />
                    </button>
                    <button
                      onClick={() => handleOpenWorkspace(workspace.id)}
                      title={`Ga naar ${workspace.name}`}
                      aria-label={`Ga naar ${workspace.name}`}
                      className="grow px-3 text-green border-l border-green/20 hover:bg-green hover:text-white"
                    >
                      <span className="shrink pr-1">{workspace.name}</span>
                      <FontAwesomeIcon icon={faArrowRight} />
                    </button>
                  </div>
                );
              }
              return (
                <button
                  key={workspace.id}
                  onClick={() => handleAdd(workspace.id)}
                  title={`Voeg toe aan ${workspace.name}`}
                  className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm first:rounded-t-md hover:bg-gray-100"
                >
                  <span className="truncate">{workspace.name}</span>
                  {isExploration(workspace) && (
                    <span className="shrink-0 text-xs text-gray-400">(verkenning)</span>
                  )}
                </button>
              );
            })
          )}
          {creating ? (
            <form
              onSubmit={handleCreateAndAdd}
              className="p-2 border-t flex flex-col gap-2 bg-gray-50 last:rounded-b-md"
            >
              <input
                autoFocus
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Naam van nieuw project"
                className="px-2 py-1 border rounded text-sm"
                disabled={busy}
              />
              {error && <p className="text-xs text-red-500">{error}</p>}
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setCreating(false);
                    setNewName("");
                    setError(null);
                  }}
                  disabled={busy}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-black"
                >
                  Annuleren
                </button>
                <button
                  type="submit"
                  disabled={busy || !newName.trim()}
                  className="px-3 py-1 bg-green text-white rounded text-sm hover:bg-dark-green disabled:opacity-50"
                >
                  {busy ? "Bezig..." : "Aanmaken"}
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setCreating(true)}
              className="block w-full text-left px-4 py-2 text-sm text-green hover:bg-gray-100 border-t last:rounded-b-md"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Nieuw project...
            </button>
          )}
        </div>
      )}
    </div>
  );
};

type DownloadOption = {
  label: string;
  icon: typeof faFilePdf;
  href: string;
  description?: string;
};

const DownloadMenu = ({ field }: { field: Cube }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selectedWorkspaceId = useWorkspaces((s) => s.selectedWorkspaceId);
  useOutsideClick([ref], () => setOpen(false), open);

  const coords = fCoords(field.center, ",");
  const archiveQuery = selectedWorkspaceId ? `?project_id=${selectedWorkspaceId}` : "";
  const options: DownloadOption[] = [
    {
      label: "Factsheet",
      icon: faFilePdf,
      href: `/report/parcel/${coords}`,
      description: "Compact rapport (PDF)",
    },
    {
      label: "Archief",
      icon: faFileArchive,
      href: `/report/parcel/${coords}/archive${archiveQuery}`,
      description: selectedWorkspaceId
        ? "Shapefile + projectbestanden (ZIP)"
        : "Shapefile van het perceel (ZIP)",
    },
  ];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`${btnBase} bg-dark-green text-white hover:bg-green`}
      >
        <FontAwesomeIcon icon={faDownload} className="w-4 h-4" />
        Download
      </button>
      {open && (
        <div className="absolute bottom-11 left-0 bg-white rounded-md shadow-lg border z-50 min-w-[240px]">
          {options.map((opt) => (
            <a
              key={opt.label}
              href={opt.href}
              onClick={() => setOpen(false)}
              className="flex items-start gap-3 px-3 py-2 text-sm hover:bg-gray-100 first:rounded-t-md last:rounded-b-md"
            >
              <FontAwesomeIcon
                icon={opt.icon}
                className="w-4 h-4 text-dark-green mt-0.5 shrink-0"
              />
              <span className="flex flex-col">
                <span className="font-semibold">{opt.label}</span>
                {opt.description && (
                  <span className="text-xs text-gray-500">{opt.description}</span>
                )}
              </span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export const CTABox = ({ field, show }: { field: Cube; show?: boolean }) =>
  !show ? null : (
    <div className="hidden sm:flex sticky bottom-0 bg-white items-center justify-center gap-2 py-3 border-t border-gray">
      <DownloadMenu field={field} />
      <AddToWorkspace field={field} />
    </div>
  );
